import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';

import { TOTE_ADDED_ONE_AT_TIME, TOTES_GENERATED } from '../../../../constants/analytics-events';
import { MAX_TOTES } from '../constants';
import { GeneratedTotesTable } from '../generated-totes-table';
import messages from './messages';
import { Segment } from '../../../../services/analytics-services/segment';
import style from './style.module.less';

/**
 * @param {object} props
 * @param {string} props.warehouseName
 * @param {boolean} props.hasError
 * @param {string} props.inventoryWarehouseId
 * @param {Array} props.generatedTotes
 * @param {(totes: Array) => void} props.setGeneratedTotes
 * @returns {JSX.Element}
 */
export const GenerateTotesDrawerContent = ({ inventoryWarehouseId, hasError, warehouseName, generatedTotes, setGeneratedTotes }) => {
  const intl = useIntl();
  const [quantity, setQuantity] = useState(10);
  const [baseName, setBaseName] = useState('');
  const [startNumber, setStartNumber] = useState(1);

  const remainingCapacity = useMemo(() => {
    return Math.max(0, MAX_TOTES - generatedTotes.length);
  }, [generatedTotes.length]);

  const isGenerateDisabled = useMemo(() => {
    return baseName.trim().length === 0 || remainingCapacity <= 0;
  }, [baseName, remainingCapacity]);

  const handleGenerate = useCallback(() => {
    if (baseName.trim().length === 0 || generatedTotes.length >= MAX_TOTES) return;

    const actualQuantity = Math.min(quantity, remainingCapacity);

    Segment.trackEvent(TOTES_GENERATED, {
      warehouseName,
      quantity: actualQuantity,
      baseName,
      startNumber,
    });

    const newTotes = [];
    let currentStartNumber = startNumber;

    // Find the highest existing number for the current base name to avoid duplicates
    const existingNumbers = generatedTotes
      .map((tote) => tote.toteName)
      .filter((name) => name.startsWith(baseName))
      .map((name) => {
        const numberPart = name.slice(baseName.length);
        const num = parseInt(numberPart, 10);
        return Number.isNaN(num) ? null : num;
      })
      .filter((num) => num !== null);

    if (existingNumbers.length > 0) {
      const maxExistingNumber = Math.max(...existingNumbers);
      currentStartNumber = Math.max(startNumber, maxExistingNumber + 1);
    }

    for (let i = 0; i < actualQuantity; i++) {
      const toteNumber = currentStartNumber + i;
      const toteNumberStr = toteNumber < 10 ? `0${toteNumber}` : toteNumber;
      newTotes.push({
        id: GuidService.newGuid(),
        toteName: `${baseName}${toteNumberStr}`,
        toteBarcode: `${DateTime.now().toMillis()}${i}`,
        warehouseName,
        inventoryWarehouseId,
      });
    }
    setGeneratedTotes((prev) => [...prev, ...newTotes]);
  }, [baseName, generatedTotes, quantity, remainingCapacity, startNumber, setGeneratedTotes, warehouseName, inventoryWarehouseId]);

  const isMaximumTotesReached = generatedTotes.length >= MAX_TOTES;

  const handleAddSingleTote = useCallback(() => {
    if (isMaximumTotesReached) return;

    Segment.trackEvent(TOTE_ADDED_ONE_AT_TIME, {
      warehouseName,
    });

    const newTote = {
      id: GuidService.newGuid(),
      toteName: '',
      toteBarcode: `${DateTime.now().toMillis()}`,
      warehouseName,
      inventoryWarehouseId,
    };

    setGeneratedTotes((prev) => [...prev, newTote]);
  }, [isMaximumTotesReached, setGeneratedTotes, inventoryWarehouseId, warehouseName]);

  return (
    <div className={style['drawer-main-content']}>
      <Text className={style['drawer-content-header']} textType={TextType.H3} value={messages.createTotesHeader} />
      <Text textType={TextType.Paragraph} value={messages.createTotesDescription} />

      <div className={style['form-section']}>
        <FlexRow className={style['form-row']}>
          <FlexColumn className={style['form-field']}>
            <Text htmlFor='totes-quantity-input' textType={TextType.Label} value={messages.quantityInputLabel} />
            <NumberInput
              className={style['number-input']}
              id='totes-quantity-input'
              max={remainingCapacity}
              min={1}
              onChange={setQuantity}
              value={quantity}
            />
          </FlexColumn>

          <Input
            className={style['text-input']}
            id='base-name-input'
            label={<Text textType={TextType.Label} value={messages.baseNameInputLabel} />}
            onChange={(e) => setBaseName(e?.target?.value)}
            placeholder={intl.formatMessage(messages.baseNameInputPlaceholder)}
            value={baseName}
            version={version2}
          />

          <FlexColumn className={style['form-field']}>
            <Text textType={TextType.Label} value={messages.startNumberInputLabel} />
            <NumberInput className={style['number-input']} min={1} onChange={setStartNumber} value={startNumber} />
          </FlexColumn>

          <FlexColumn className={style['form-field']}>
            <Button
              className={style['generate-button']}
              disabled={isGenerateDisabled}
              onClick={handleGenerate}
              theme={THEME_CONSTANTS.secondary}
            >
              <FormattedMessage {...messages.generateTotesButton} />
            </Button>
          </FlexColumn>
        </FlexRow>
      </div>

      {generatedTotes.length === 0 && (
        <div className={style['add-single-section']}>
          <Button className={style['add-single-button']} onClick={handleAddSingleTote} theme={THEME_CONSTANTS.link}>
            <FontAwesomeIcon className={style['plus-icon']} icon={faPlus} />
            <Text element='span' textType={TextType.Paragraph} value={messages.addSingleToteButton} />
          </Button>
        </div>
      )}
      <FlexColumn className={style['table-section']} gap={Gap.Space16}>
        {hasError && (
          <BannerMessageV2
            bannerContainerClassName={style['banner-container']}
            className={style.banner}
            content={<FormattedMessage {...messages.errorAddingTotesBanner} />}
            status={BannerStatus.Error}
          />
        )}
        {generatedTotes.length > 0 && (
          <GeneratedTotesTable
            generatedTotes={generatedTotes}
            isAddToteDisabled={isMaximumTotesReached}
            onAddTote={handleAddSingleTote}
            setGeneratedTotes={setGeneratedTotes}
            warehouseName={warehouseName}
          />
        )}
      </FlexColumn>
    </div>
  );
};
